import { ReportPdfData } from '../types';
import { createEyebrow, createFooter } from '../utils';
import { PdfAssets } from '../../pdfTemplates';

/**
 * Creates the destinations pages for the PDF report
 * @param data Report data
 * @param assets Logo and font assets for the PDF
 * @param colors Color palette for styling
 * @param pageNumber Current page number
 * @param totalPages Total number of pages
 * @returns Destinations pages content for pdfmake
 */
export const createDestinationsPages = (
  data: ReportPdfData,
  assets: PdfAssets | undefined,
  colors: Record<string, string>,
  pageNumber: number,
  totalPages: number
) => {
  // Check if there are any destinations
  if (!data.destinations || data.destinations.length === 0) {
    return [];
  }
  
  // Ensure selectedSections is an array
  const selectedSections = Array.isArray(data.selectedSections) ? data.selectedSections : [];
  
  // Filter destinations based on selected sections
  const filteredDestinations = data.destinations.filter(dest => {
    // If DESTINATIONS is selected, include all destinations
    if (selectedSections.includes('DESTINATIONS')) {
      return true;
    }
    
    // If it's Facebook Pixel, only include if it's in the selected sections
    if (dest.name === 'Facebook Pixel' || dest.name === 'Meta Pixel') {
      return selectedSections.includes('Facebook Pixel') || 
             selectedSections.includes('DESTINATIONS:Facebook Pixel') ||
             selectedSections.includes('Meta Pixel') ||
             selectedSections.includes('DESTINATIONS:Meta Pixel');
    }
    
    // For other destinations, check if they have nodes and if any of those nodes are in selected sections
    if (dest.nodes && dest.nodes.length > 0) {
      for (const node of dest.nodes) {
        if (node.id && (
            selectedSections.includes(`DESTINATION:${node.id}`) ||
            selectedSections.includes(node.id)
        )) {
          return true;
        }
      }
    }
    
    // Check if the destination itself is selected
    if (selectedSections.includes(dest.name) || 
        selectedSections.includes(`DESTINATIONS:${dest.name}`)) {
      return true;
    }
    
    // By default, don't include the destination if no specific selection is found
    return false;
  });
  
  // If no destinations after filtering, return empty array
  if (filteredDestinations.length === 0) {
    return [];
  }
  
  // Group destinations by category
  const destinationsByCategory: Record<string, any[]> = {};
  
  filteredDestinations.forEach(dest => {
    const category = dest.category || 'Other';
    if (!destinationsByCategory[category]) {
      destinationsByCategory[category] = [];
    }
    destinationsByCategory[category].push(dest);
  });
  
  // Create pages with 2 destinations per page
  const destinationsPerPage = 2;
  const pages: any[][] = [];
  let currentPageDestinations: any[] = [];
  let currentPageNumber = pageNumber;
  
  // Create destination content
  const createDestinationContent = (dest: any, index: number, isLastOnPage: boolean) => {
    const category = dest.category || 'Other';
    const supportsSyncInjector = dest.supportsSyncInjector !== undefined ? dest.supportsSyncInjector : true;
    
    // Get nodes using this integration
    const nodesUsingIntegration = dest.nodes || [];
    
    return {
      stack: [
        // Destination divider (except for first destination)
        index > 0 ? {
          canvas: [
            {
              type: 'line',
              x1: 0,
              y1: 0,
              x2: 467,
              y2: 0,
              lineWidth: 1,
              lineColor: colors.lightGrey
            }
          ],
          margin: [64, 0, 0, 32]
        } : null,
        
        // Destination content
        {
          columns: [
            {
              stack: [
                // Integration logo and name with increased margin
                {
                  columns: [
                    {
                      // Use the actual icon if available, otherwise show placeholder
                      ...(dest.icon ? {
                        image: dest.icon,
                        width: 24,
                        height: 24,
                      } : {
                        canvas: [
                          {
                            type: 'rect',
                            x: 0,
                            y: 0,
                            w: 24,
                            h: 24,
                            color: colors.lightGrey
                          }
                        ],
                      }),
                      width: 24,
                    },
                    {
                      text: dest.name, // Integration name
                      fontSize: 16, // Increased font size
                      bold: true,
                      color: colors.black,
                      margin: [6, 1, 0, 0]
                    }
                  ],
                  margin: [0, 0, 0, 4] // Increased bottom margin
                },
                
                // Category and sync injector support with more space
                {
                  stack: [
                    {
                      text: category.toUpperCase(),
                      fontSize: 7,
                      color: colors.mediumGrey,
                      margin: [0, 0, 0, 2] // Added margin between category and sync injector
                    },
                    {
                      text: supportsSyncInjector ? 'SUPPORTS SYNC INJECTOR' : 'NO SYNC INJECTOR SUPPORT',
                      fontSize: 7,
                      color: colors.mediumGrey
                    }
                  ],
                  margin: [0, 0, 0, 10]
                },
                
                // Description
                {
                  text: dest.description || `${dest.name} integration allows you to send your data to ${dest.name.toLowerCase()} platform for analysis and activation.`,
                  fontSize: 9,
                  color: colors.darkGrey,
                  margin: [0, 0, 0, 16]
                },
                
                // Documentation links - handle different formats with proper type guards
                // Only show documentation links if they exist
                ...(dest.docs ? (
                  typeof dest.docs === 'string' ? [
                    {
                      text: 'View Documentation',
                      fontSize: 9,
                      color: colors.primaryDark,
                      decoration: 'underline',
                      link: dest.docs,
                      margin: [0, 0, 0, 0]
                    }
                  ] : Array.isArray(dest.docs) ? 
                    dest.docs.map((docItem: any, docIndex: number) => {
                      const label = typeof docItem === 'object' && docItem.label ? docItem.label : 'View Documentation';
                      const url = typeof docItem === 'object' && docItem.url ? docItem.url : typeof docItem === 'string' ? docItem : '';
                      
                      return {
                        text: label,
                        fontSize: 9,
                        color: colors.primaryDark,
                        decoration: 'underline',
                        link: url,
                        margin: [0, 0, 0, docIndex < (Array.isArray(dest.docs) ? dest.docs.length : 0) - 1 ? 4 : 0]
                      };
                    })
                  : dest.docs && typeof dest.docs === 'object' && 'links' in dest.docs ?
                    (dest.docs as { links: Array<{ label: string, url: string }> }).links.map((link: { label: string, url: string }, linkIndex: number) => ({
                      text: link.label || 'View Documentation',
                      fontSize: 9,
                      color: colors.primaryDark,
                      decoration: 'underline',
                      link: link.url,
                      margin: [0, 0, 0, linkIndex < (dest.docs && typeof dest.docs === 'object' && 'links' in dest.docs ? (dest.docs as { links: any[] }).links.length : 0) - 1 ? 4 : 0]
                    }))
                  : []  // Empty array if docs exists but in an unrecognized format
                ) : [])  // Empty array if no docs
              ],
              width: '60%',
              margin: [0, 0, 12, 0]
            },
            {
              stack: [
                // If there are no nodes using this integration, show a message
                ...(nodesUsingIntegration.length === 0 ? [
                  {
                    text: 'No nodes are currently using this integration.',
                    fontSize: 9,
                    color: colors.darkGrey,
                    margin: [0, 0, 0, 8]
                  }
                ] : 
                // Otherwise, list all nodes using this integration
                nodesUsingIntegration.map((node: any, nodeIndex: number) => {
                  const nodeDetails = [];
                  
                  // Node name
                  nodeDetails.push({
                    text: node.label || 'Unnamed Node',
                    fontSize: 11,
                    bold: true,
                    color: colors.black,
                    margin: [0, nodeIndex > 0 ? 16 : 0, 0, 2]
                  });
                  
                  // Only show status if it exists
                  if (node.status) {
                    nodeDetails.push({
                      text: `Status: ${node.status}`,
                      fontSize: 9,
                      color: colors.darkGrey,
                      margin: [0, 0, 0, 1]
                    });
                  }
                  
                  // Only show custom label if it exists
                  if (node.customLabel) {
                    nodeDetails.push({
                      text: `Custom Label: ${node.customLabel}`,
                      fontSize: 9,
                      color: colors.darkGrey,
                      margin: [0, 0, 0, 1]
                    });
                  }
                  
                  // Add any other node-specific fields that exist
                  // Team access
                  if (node.teamAccess) {
                    nodeDetails.push({
                      text: `Team Access: ${node.teamAccess}`,
                      fontSize: 9,
                      color: colors.darkGrey,
                      margin: [0, 0, 0, 1]
                    });
                  }
                  
                  // Schema
                  if (node.schema) {
                    nodeDetails.push({
                      text: `Schema: ${node.schema}`,
                      fontSize: 9,
                      color: colors.darkGrey,
                      margin: [0, 0, 0, 1]
                    });
                  }
                  
                  return nodeDetails;
                })).flat()
              ],
              width: '40%',
              margin: [12, 0, 0, 0]
            }
          ],
          margin: [64, 0, 40, isLastOnPage ? 0 : 32]
        }
      ]
    };
  };
  
  // Process all destinations and create pages
  filteredDestinations.forEach((dest, index) => {
    currentPageDestinations.push(createDestinationContent(
      dest, 
      currentPageDestinations.length, 
      (currentPageDestinations.length === destinationsPerPage - 1) || (index === filteredDestinations.length - 1)
    ));
    
    // If we've reached the max destinations per page or this is the last destination
    if (currentPageDestinations.length === destinationsPerPage || index === filteredDestinations.length - 1) {
      // Create a page with the current destinations
      pages.push([
        // Eyebrow section
        ...createEyebrow('Architecture Breakdown - Destinations', assets?.markUrl, colors),
        
        // Content section
        {
          stack: [
            {
              text: 'Destinations',
              fontSize: 18,
              bold: true,
              color: colors.black,
              margin: [64, 48, 0, 32]
            },
            
            // Individual destinations
            ...currentPageDestinations
          ]
        },
        
        // Footer
        createFooter(currentPageNumber, totalPages, colors),
        
        // Always add page break after the last destination page to ensure next steps starts on a new page
        { text: '', pageBreak: 'after' }
      ]);
      
      // Reset for next page
      currentPageDestinations = [];
      currentPageNumber++;
    }
  });
  
  // Flatten the array of pages
  return pages.flat().filter(Boolean);
};
